// Mirrors core.stores / core.staff / core.businesses / core.business_users /
// core.devices in CliqSuite_DB_Architecture.dc.html.
import { z } from "zod";

// Structured shape for core.stores.business_hours (still a free-form jsonb
// column, no migration needed — this just tightens what the app writes/reads
// into it). One entry per day; `staff_start`, when set, is when clock-in
// opens for that day — earlier than `open` for stores that need prep time
// before customers arrive. Falls back to `open` when unset.
export const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type DayKey = (typeof DAY_KEYS)[number];
export const dayHoursSchema = z.object({
  open: z.string(),
  close: z.string(),
  closed: z.boolean().default(false),
  staff_start: z.string().nullable().default(null),
});
export type DayHours = z.infer<typeof dayHoursSchema>;
export const businessHoursSchema = z.record(z.string(), dayHoursSchema).default({});
export type BusinessHours = z.infer<typeof businessHoursSchema>;

export const storeSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  name: z.string(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  // Postgres numeric columns, so string like tax_rate below — the
  // geofence gate (guest-portal scanning a table QR) only kicks in once
  // both are set.
  latitude: z.string().nullable(),
  longitude: z.string().nullable(),
  // Overrides the app's own default geofence radius (POS clock-in and
  // guest-portal table-scan share this one value) for stores that need it
  // tighter — e.g. two neighboring businesses close enough that the
  // default radius would cover both. Null keeps each check's own built-in
  // default (POS's 500m, guest-portal's 300m) exactly as before.
  geofence_radius_m: z.number().int().nullable(),
  store_icon: z.string().default("storefront"),
  business_hours: businessHoursSchema,
  currency_code: z.string().default("MMK"),
  // Postgres numeric column — arrives over Electric/pg as a string (same
  // reason crm.ts's earn_rate etc. are z.string()), not a real number. A
  // full-row update() validates whatever's currently synced for every
  // field, so declaring this z.number() made any update touching an
  // unrelated column (e.g. guest_portal_enabled) fail validation.
  tax_rate: z.string().default("0.05"),
  tax_label: z.string().default("PB"),
  currency_precision: z.union([z.literal(0), z.literal(2), z.literal(3)]).default(0),
  guest_portal_enabled: z.boolean().default(false),
  employee_discount_enabled: z.boolean().default(false),
  employee_discount_percent: z.number().int().min(1).max(100).default(10),
  is_active: z.boolean().default(true),
  sort_order: z.number().int().default(0),
  settings: z.record(z.string(), z.unknown()).default({}),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type Store = z.infer<typeof storeSchema>;

// Full row, including pin_hash — used only server-side (sync-api) for
// inserts/updates and PIN verification. Never synced to clients.
export const staffSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  store_id: z.string().uuid().nullable(),
  name: z.string(),
  role: z.string().default("Cashier"),
  pin_hash: z.string(),
  avatar_color: z.string().nullable(),
  is_manager: z.boolean().default(false),
  is_active: z.boolean().default(true),
  base_salary: z.number().int().default(0),
  // For the owner to reach them; shown in Owner, Team.
  phone: z.string().nullable().default(null),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type Staff = z.infer<typeof staffSchema>;
/** The owner's own register profile (made at signup, role "Owner", which sync-api reserves for it). Not an employee: no seat, no salary, not in Team. */
export const isOwnerStaff = (staff: Pick<Staff, "role">) => staff.role.trim().toLowerCase() === "owner";

// Row shape actually synced to clients via Electric — pin_hash is excluded
// both here and from the shape's `columns` allowlist (see
// packages/db/src/collections/core.ts). PIN checks happen through
// sync-api's POST /core/staff/:id/verify-pin instead.
export const staffPublicSchema = staffSchema.omit({ pin_hash: true });
export type StaffPublic = z.infer<typeof staffPublicSchema>;

export const businessSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  business_type: z
    .enum([
      "restaurant",
      "cafe",
      "retail",
      "apparel",
      "pharmacy",
      "electronics",
      "supermarket",
      "convenience",
      "salon",
      "spa",
      "barber",
      "gym",
      "clinic",
      "studio",
    ])
    .default("restaurant"),
  logo_url: z.string().nullable(),
  billing_email: z.string().nullable(),
  subscription_plan_id: z.string().uuid().nullable(),
  subscription_status: z
    .enum(["active", "inactive", "expired", "suspended", "trial"])
    .default("inactive"),
  subscription_period_end: z.string().nullable(),
  grace_period_ends_at: z.string().nullable(),
  // Written by an admin, shown to the owner on the suspended screen.
  suspension_reason: z.string().nullable().default(null),
  installed_modules: z.array(z.string()).default([]),
  settings: z.record(z.string(), z.unknown()).default({}),
  bonus_point_value: z.number().int().default(0),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type Business = z.infer<typeof businessSchema>;

// Full row (with password_hash) — server-side only, see staffSchema note above.
export const businessUserSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  staff_id: z.string().uuid().nullable().default(null),
  email: z.string(),
  password_hash: z.string(),
  name: z.string(),
  // Free text (soft reference into core.roles.name) — same unification as
  // core.staff.role, not a hardcoded owner/manager enum anymore.
  role: z.string().default("owner"),
  // Empty = every store; non-empty = restricted to just these (enforced
  // client-side in owner-portal's storeScope.tsx, not a hard server
  // boundary — matching the rest of Access Control's permission model).
  store_ids: z.array(z.string().uuid()).default([]),
  is_active: z.boolean().default(true),
  last_login_at: z.string().nullable(),
  // Null = never proved ownership of this email (see core.email_tokens,
  // services/sync-api's lib/email.ts) — soft-gate only, never blocks login.
  email_verified_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type BusinessUser = z.infer<typeof businessUserSchema>;
export const businessUserPublicSchema = businessUserSchema.omit({ password_hash: true });
export type BusinessUserPublic = z.infer<typeof businessUserPublicSchema>;

// Full row (with password_hash) for platform.admin_users — server-side only.
export const adminUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  password_hash: z.string(),
  name: z.string(),
  role: z.string().default("admin"),
  is_active: z.boolean().default(true),
  last_login_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type AdminUser = z.infer<typeof adminUserSchema>;
export const adminUserPublicSchema = adminUserSchema.omit({ password_hash: true });
export type AdminUserPublic = z.infer<typeof adminUserPublicSchema>;

// No secrets on this table — safe to sync in full. Pairing codes are
// low-sensitivity (physical possession of the device is the real gate).
export const deviceSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  store_id: z.string().uuid(),
  pairing_code: z.string(),
  device_type: z.enum(["pos", "kds", "services"]),
  register_name: z.string(),
  is_paired: z.boolean().default(false),
  bypass_geofence: z.boolean().default(false),
  device_name: z.string().nullable(),
  last_seen_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type Device = z.infer<typeof deviceSchema>;

export const storePaymentMethodSchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  type: z.enum(["cash", "card", "qr", "bank", "wallet"]),
  name: z.string(),
  image_data: z.string().nullable(),
  instructions: z.string().nullable(),
  bank_name: z.string().nullable(),
  account_name: z.string().nullable(),
  account_number: z.string().nullable(),
  branch: z.string().nullable(),
  network: z.string().nullable(),
  wallet_address: z.string().nullable(),
  is_enabled: z.boolean().default(true),
  sort_order: z.number().int().default(0),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type StorePaymentMethod = z.infer<typeof storePaymentMethodSchema>;

export const staffSessionSchema = z.object({
  id: z.string().uuid(),
  staff_id: z.string().uuid(),
  store_id: z.string().uuid(),
  device_id: z.string().uuid().nullable(),
  clocked_in_at: z.string(),
  clocked_out_at: z.string().nullable(),
  duration_minutes: z.number().int().nullable(),
  break_minutes: z.number().int().default(0),
  source: z.enum(["pos", "kds", "manual"]).default("pos"),
  notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type StaffSession = z.infer<typeof staffSessionSchema>;

// Access Control's editable role/permission definitions. business_users.role
// stores the role NAME as a plain string (soft reference) — this table is
// the source of truth for which roles exist and what they grant.
export const roleSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  permissions: z.array(z.string()).default([]),
  is_system: z.boolean().default(false),
  icon: z.string().default("shield_person"),
  color: z.string().default("#2D6FE0"),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Role = z.infer<typeof roleSchema>;

/**
 * Starter role set — inserted for every new business at signup (see
 * sync-api's owner-signup handler) so a fresh owner can add staff
 * immediately, without a detour to Access Control first. Owner Portal's
 * own "Set up default roles" button (Access Control's Roles tab, shown
 * when a business somehow has zero roles) inserts this exact same list,
 * so the two stay in sync by construction rather than by hand-maintained
 * duplication. `permissions` values are free-form labels matched against
 * Owner Portal's own permission catalog (apps/owner-portal's
 * AccessPanels.tsx) — not enforced by any DB constraint, since a
 * business is free to rename/redefine what each grants.
 */
export const DEFAULT_ROLES: Array<{
  name: string;
  description: string;
  permissions: string[];
  is_system: boolean;
  icon: string;
  color: string;
}> = [
  {
    name: "Owner",
    description: "Full access to everything",
    permissions: ["Operations & menu", "Inventory & staff", "View reports", "Payroll", "User management", "Billing & plan", "Delete data"],
    is_system: true,
    icon: "workspace_premium",
    color: "#8B5CC7",
  },
  { name: "Manager", description: "Store-scoped operational access", permissions: ["Operations & menu", "Inventory & staff", "View reports"], is_system: true, icon: "badge", color: "#2D6FE0" },
  { name: "Staff", description: "POS, kitchen, and service-app access", permissions: [], is_system: true, icon: "person", color: "#21A35B" },
];

// Weekly recurring shift template — Staff & Scheduling's "Schedule" tab.
// Distinct from staff_sessions (real-time clock in/out, "On Shift" tab).
export const staffShiftSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  store_id: z.string().uuid(),
  staff_id: z.string().uuid(),
  day_of_week: z.enum(["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]),
  start_time: z.string(),
  end_time: z.string(),
  role: z.string().nullable().default(null),
  // The Monday (YYYY-MM-DD) of the one week this shift is for; null repeats every week.
  only_week: z.string().nullable().default(null),
  created_at: z.string(),
  updated_at: z.string(),
});
export type StaffShift = z.infer<typeof staffShiftSchema>;

// Payroll run history — mirrors the mockup's model (sum of active staff's
// base_salary, run per pay cycle).
export const payrollRunSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  // null = whole-business run (all active staff); set = an individual
  // "Pay this employee" run (headcount always 1).
  staff_id: z.string().uuid().nullable().default(null),
  period_label: z.string(),
  schedule: z.enum(["weekly", "biweekly", "monthly"]).default("monthly"),
  status: z.enum(["pending", "paid"]).default("paid"),
  total: z.number().int().default(0),
  headcount: z.number().int().default(0),
  paid_at: z.string().nullable(),
  created_at: z.string(),
});
export type PayrollRun = z.infer<typeof payrollRunSchema>;

// Owner-defined bonus rules — manual only (no automatic condition tracking).
// Points convert to cash via businesses.bonus_point_value, added to base
// salary in Payroll.
export const bonusRuleSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  label: z.string(),
  icon: z.string().default("star"),
  points: z.number().int().default(10),
  is_active: z.boolean().default(true),
  sort_order: z.number().int().default(0),
  created_at: z.string(),
  updated_at: z.string(),
});
export type BonusRule = z.infer<typeof bonusRuleSchema>;

// A manual, per-staff, per-month award of one bonus rule. `period` is
// "YYYY-MM" — awards are always granted for the current calendar month.
export const bonusAwardSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  staff_id: z.string().uuid(),
  rule_id: z.string().uuid(),
  period: z.string(),
  awarded_at: z.string(),
});
export type BonusAward = z.infer<typeof bonusAwardSchema>;

// Per-business enabled/config state for the fixed integration catalog
// (Telegram, Discord, webhooks, ...) — the catalog itself (name/icon/fields/
// events) is static app config in Owner Portal, not stored here.
export const businessIntegrationSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  integration_key: z.string(),
  enabled: z.boolean().default(false),
  config: z.record(z.string(), z.unknown()).default({}),
  // Scheduler-owned (see services/sync-api's telegram.ts daily summary job)
  // — deliberately a dedicated column, not a key inside `config`, since
  // Owner Portal's IntegrationDrawer treats `config` as its own fully-owned
  // local object and PATCHes it as a full replace; a marker stored there
  // would get silently clobbered the next time an owner re-saves any field.
  last_daily_summary_sent_on: z.string().nullable().default(null),
  created_at: z.string(),
  updated_at: z.string(),
});
export type BusinessIntegration = z.infer<typeof businessIntegrationSchema>;
